import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchTicker } from "@/lib/api";

const HIDDEN = [/^\/kids/, /^\/watch\//, /^\/jw-test\//, /^\/shorts\/feed/];
const CYCLE_MS = 5000;
const KIND_STYLE = {
  film: "text-amber border-amber/50",
  sports: "text-sky-300 border-sky-400/50",
  knicks: "text-[#FFB070] border-[#F58426]/70 bg-[#006BB6]/25",
};

// One headline at a time with a cross-fade; data re-polled every 60s without a page refresh.
export const LiveTicker = () => {
  const { pathname } = useLocation();
  const hidden = HIDDEN.some((r) => r.test(pathname));
  const { data } = useQuery({ queryKey: ["ticker"], queryFn: fetchTicker, refetchInterval: 60000, enabled: !hidden });
  const items = data?.items || [];
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), CYCLE_MS);
    return () => clearInterval(t);
  }, [paused, items.length]);

  if (hidden || !items.length) return null;
  const item = items[idx % items.length];
  return (
    <div
      data-testid="live-ticker"
      className="border-t border-white/5 bg-black/40"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto flex h-8 max-w-[1600px] min-[2200px]:max-w-[2200px] items-center gap-3 px-4 sm:px-8">
        <span className="flex shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-crimson">
          <span className="rv-ticker-dot h-1.5 w-1.5 rounded-full bg-crimson" aria-hidden="true" />
          Live
        </span>
        <Link
          key={item.video_id}
          to={`/watch/${item.video_id}`}
          data-testid="live-ticker-item"
          className="rv-ticker-item group flex min-w-0 items-center gap-2.5 focus:outline-none"
        >
          <span className={`shrink-0 border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${KIND_STYLE[item.kind] || KIND_STYLE.film}`}>
            {item.label}
          </span>
          <span className="truncate text-[12px] text-white/80 transition-colors duration-300 group-hover:text-white group-focus:text-amber">
            {item.title}
          </span>
        </Link>
        <span data-testid="live-ticker-position" className="ml-auto hidden shrink-0 font-mono text-[10px] text-white/30 sm:inline">
          {(idx % items.length) + 1}/{items.length}
        </span>
      </div>
    </div>
  );
};
